import type {InvalidHoverReason} from 'src/components/analytics-genesis/byod/pages/dashboard-builder/canvas/tile-row';

/** Subset of drop-result fields that `decideDragOutcome` actually reads.
 *  Built from @dnd-kit event data in canvas.tsx before being passed here. */
export interface DragEndResult {
  source: {droppableId: string; index: number};
  destination: {droppableId: string; index: number} | null;
  draggableId: string;
  reason: 'DROP' | 'CANCEL';
  /** The drag type (e.g. 'kpi-tiles' or 'viz-tiles') used for type-mismatch
   *  rejection when collision detection gives a destination in an incompatible
   *  row. */
  dragType?: string;
  /** Row types keyed by row id — used to validate that the destination row
   *  accepts the dragged tile type. */
  rowTypes?: Record<string, string>;
}

/** Prefix used by the inter-row drop zones (`new-row-{kpi|viz}-after-{N}`). */
export const NEW_ROW_PREFIX = 'new-row-';

/** Suffix used by the row-end sentinel droppables (`{rowId}__end`). */
export const ROW_END_SUFFIX = '__end';

/** Matches new-row droppable ids: `new-row-kpi-after-3` → ['kpi','3']. */
const NEW_ROW_REGEX = /^new-row-(kpi|viz)-after-(-?\d+)$/;

/**
 * The decision space for a drop. Side effects (store mutations, toasts,
 * screen-reader announcements) are the consumer's responsibility — this
 * function is pure so it's directly testable with synthetic DropResults.
 */
export type DragOutcome =
  | {kind: 'rejected'; reason: InvalidHoverReason}
  | {kind: 'cancelled'}
  | {kind: 'returned'}
  | {kind: 'same-position'}
  | {kind: 'no-op'}
  | {
      kind: 'move-tile';
      fromRowId: string;
      toRowId: string;
      fromIndex: number;
      toIndex: number;
    }
  | {
      kind: 'move-to-new-row';
      fromRowId: string;
      fromIndex: number;
      afterIndex: number;
      newRowType: 'kpi' | 'viz';
    };

/**
 * Decide what should happen at the end of a drag. Pure function over the
 * `DropResult` and the canvas's `hoveredInvalid` snapshot at drop time.
 *
 * Branch ordering matters:
 *   1. No destination + invalid hover → user dropped on a row we already
 *      flagged as a wrong target (type mismatch / row full). Surface as a
 *      rejection with the original reason.
 *   2. No destination + reason === 'CANCEL' → user pressed Escape.
 *   3. No destination → drop missed any droppable; tile snaps back.
 *   3b. Has destination + reason === 'CANCEL' → @dnd-kit preserves the
 *       target on Escape, so catch it before the move logic.
 *   4. Same source position → no work, but the consumer still announces
 *      to keep the screen-reader narrative coherent.
 *   5. Destination starts with `NEW_ROW_PREFIX` → parse the inter-row
 *      drop zone id; if it doesn't match the regex, treat as no-op
 *      (defensive — only this code mints those ids).
 *   6. Otherwise → ordinary same-type cross-row or in-row reorder.
 */
export function decideDragOutcome(
  result: DragEndResult,
  invalidAtDrop: {rowId: string; reason: InvalidHoverReason} | null,
): DragOutcome {
  const {source, destination, reason, dragType, rowTypes} = result;

  if (!destination) {
    if (invalidAtDrop) {
      return {kind: 'rejected', reason: invalidAtDrop.reason};
    }
    if (reason === 'CANCEL') {
      return {kind: 'cancelled'};
    }
    return {kind: 'returned'};
  }

  // @dnd-kit preserves the target even on Escape, so a cancelled drag can
  // arrive with a non-null destination. Catch it here before the move logic.
  if (reason === 'CANCEL') {
    return {kind: 'cancelled'};
  }

  // Reject drops into incompatible rows (defense-in-depth — the `accept`
  // prop on sortables should prevent this, but guard here too so the
  // consumer can surface a toast instead of a silent no-op in the store).
  if (invalidAtDrop && destination.droppableId === invalidAtDrop.rowId) {
    return {kind: 'rejected', reason: invalidAtDrop.reason};
  }

  if (dragType && rowTypes) {
    const destRowType = rowTypes[destination.droppableId];
    if (destRowType !== undefined) {
      const expectedType = destRowType === 'kpi' ? 'kpi-tiles' : 'viz-tiles';
      if (dragType !== expectedType) {
        return {
          kind: 'rejected',
          reason: {kind: 'type-mismatch', rowType: destRowType},
        };
      }
    }
  }

  if (
    source.droppableId === destination.droppableId &&
    source.index === destination.index
  ) {
    return {kind: 'same-position'};
  }

  if (destination.droppableId.startsWith(NEW_ROW_PREFIX)) {
    const match = destination.droppableId.match(NEW_ROW_REGEX);
    if (!match) {
      return {kind: 'no-op'};
    }
    return {
      kind: 'move-to-new-row',
      fromRowId: source.droppableId,
      fromIndex: source.index,
      afterIndex: parseInt(match[2], 10),
      newRowType: match[1] as 'kpi' | 'viz',
    };
  }

  return {
    kind: 'move-tile',
    fromRowId: source.droppableId,
    toRowId: destination.droppableId,
    fromIndex: source.index,
    toIndex: destination.index,
  };
}
